import { Group, Stack, Badge, Tabs, Text } from "@mantine/core"
import { Loader } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import { CertificateByOrganizationDto } from "~/gen/api"
import { useFindDocumentUrlQuery } from "~/hooks/useDocumentsQuery"
import { CertificateForm } from "~/pages/Brand/Certificates/components/CertificateForm.tsx"
import { CertificateRequestsValidationList } from "~/pages/Brand/Certificates/components/CertificateRequestsValidationList"

type Props = {
  certificate: CertificateByOrganizationDto
  onClose: () => void
  opened: boolean
}

export const CertificateVerificationDrawer = ({
  certificate,
  onClose,
  opened,
}: Props) => {
  const { t } = useTranslation("certificates")

  const [activeTab, setActiveTab] = useState<"summary" | "requests">("summary")

  const { data: documentUrl, isPending: isLoading } = useFindDocumentUrlQuery(
    certificate.documentId,
    {
      enabled: !!certificate.documentId,
      refetchOnWindowFocus: false,
    }
  )

  if (!certificate.documentId || !documentUrl) {
    return null
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <CustomDrawer
      size="90%"
      h="100vh"
      onClose={onClose}
      opened={opened}
      drowerBodyProps={{ style: { overflow: "hidden" } }}
    >
      <Group h="100%" p={0} gap={0} align="start">
        <Stack
          w="50%"
          h="100%"
          p="lg"
          bg="var(--mantine-color-brand-background)"
        >
          {activeTab === "summary" ? (
            <iframe
              src={documentUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="Document Viewer"
            />
          ) : (
            <CertificateForm certificate={certificate} />
          )}
        </Stack>
        <Stack
          w="50%"
          h="100%"
          px="lg"
          pb={0}
          bg="var(--mantine-color-brand-background)"
        >
          <Tabs
            value={activeTab}
            onChange={(value) =>
              setActiveTab(z.enum(["summary", "requests"]).parse(value))
            }
            h="100%"
          >
            <Tabs.List
              pos="sticky"
              top={0}
              bg="var(--mantine-color-brand-background)"
              style={{ zIndex: 2 }}
            >
              <Tabs.Tab value="summary">
                <Text fw={600}>{t("modal.summary.tab.title")}</Text>
              </Tabs.Tab>
              <Tabs.Tab value="requests">
                <Group>
                  <Text fw={600}>{t("modal.associatedRequests.title")}</Text>
                  <Badge color="blue">
                    {certificate.certificateRequests.length}
                  </Badge>
                </Group>
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="summary" style={{ height: "92%" }}>
              <CertificateForm certificate={certificate} />
            </Tabs.Panel>
            <Tabs.Panel value="requests" style={{ height: "100%" }}>
              <CertificateRequestsValidationList certificate={certificate} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Group>
    </CustomDrawer>
  )
}
